import { queryClient } from 'common/providers/QueryProvider';
import { locale } from '@tcc/i18n';

import type { Activity } from 'store/types/types';
import type { FieldData } from '../common/types';
import type {
  PrescriptionGenerationJobQueryResponse
} from '../common/hooks/useBulkGeneratePlantingPrescription/types';

import { convert } from 'util/unitConversion';
import { formatDateHyphenatedByLocale, formatDateToISO8601 } from 'util/formatters/dateFormatter';
import { kebabCase, snakeCase } from 'util/string';
import { generateZoneId } from 'util/zoneCreation';

import { JOB_STATUS_KEY } from '../common/hooks/useBulkGeneratePlantingPrescription/const';
import { UNIT_TYPES } from 'constants/unitTypes';
import { PrescriptionZone } from 'mapping/types';


export const buildFeatures = (zonesWithIds: {
  zone: PrescriptionZone;
  id: string;
}[], cropNominalWeight: number) => {
  const features = zonesWithIds.map(({ zone, id }, index) => {
    const { seedingRate, targetYield, geometry, product } = zone;
    const convertedRate = convert({
      from: { u: seedingRate.u, q: seedingRate.v },
      to: { u: UNIT_TYPES.seed_hectare }
    });

    const convertedYield = convert({
      cropNominalWeight,
      from: { u: targetYield.u, q: targetYield.v },
      to: { u: UNIT_TYPES.kg_hectare }
    });

    return {
      type: 'Feature',
      geometry,
      properties: {
        description: index + 1,
        liquid: null,
        product: {
          id: product.id,
          name: product.name,
          brand: product.brand.name
        },
        rate: convertedRate,
        'target-yield': convertedYield,
        'zone-id': id,
        'is-check-strip': false
      }
    };
  });

  return features;
};

const buildZonesSummaries = (zones: PrescriptionZone[]) => {
  const generatedZoneMap: Record<string, true> = {};

  const zonesWithIds = zones.map((zone) => {
    const generatedId = generateZoneId(generatedZoneMap);
    generatedZoneMap[generatedId] = true;
    return {
      zone,
      id: generatedId
    };
  });

  const zonesSummaries = zonesWithIds.map(({ zone, id }) => ({
    id,
    acres: convert({
      from: { u: zone.area.u, q: zone.area.v },
      to: { u: UNIT_TYPES.acre }
    }).q
  }));

  return {
    zonesWithIds,
    zonesSummaries
  };
};

const parseProductNameForScriptName = (productName: string) => {
  return productName.replace(/[^a-zA-Z0-9]/g,'-').substring(0, 8);
};

interface GetScriptNameParams {
  fieldName: string;
  productName: string;
  bulkPrefix: string;
}
// This mimics what getDefaultScriptName (src/js/planting-plan-form/selectors.js) does for planting prescriptions.
export const getScriptName = ({ fieldName, productName, bulkPrefix }: GetScriptNameParams) => {
  const fieldNameString = kebabCase(fieldName).substring(0, 12);
  const productsString = parseProductNameForScriptName(productName);
  const dateString = formatDateHyphenatedByLocale(new Date(), locale);
  const prefixString = snakeCase(bulkPrefix);

  return `${prefixString}-${fieldNameString}-${productsString}-${dateString}`;
};

interface ParsedPrescriptionParams {
  activities: Activity[];
  cropNominalWeight: number;
  fields: FieldData[];
  plannedPlantingDateString: string;
  bulkPrefix: string;
}

export const parsePrescriptionsToSave = ({
  fields,
  activities,
  cropNominalWeight,
  plannedPlantingDateString,
  bulkPrefix
}: ParsedPrescriptionParams) => {
  const prescriptionsGenerationData = queryClient.getQueriesData<PrescriptionGenerationJobQueryResponse>({
    queryKey: [JOB_STATUS_KEY]
  });

  const prescriptionsToSave = fields
  .filter((field) =>
    prescriptionsGenerationData.find(
      ([[, fieldId], data]) => fieldId === field.id && data?.prescriptionGenerationJob.result?.zones
    ))
  .map((field) => {
    const prescriptionId = crypto.randomUUID();
    const activity = activities.find(({
      date,
      'field-uuid': fieldUuid
    }) => fieldUuid === field.id && getDateString(new Date(date)) === plannedPlantingDateString);

    const { prescriptionGenerationJob } = queryClient.getQueryData<PrescriptionGenerationJobQueryResponse>([
      JOB_STATUS_KEY,
      field.id
    ]);

    const { zonesWithIds, zonesSummaries } = buildZonesSummaries(prescriptionGenerationJob.result.zones);
    const features = buildFeatures(zonesWithIds, cropNominalWeight);
    const name = getScriptName({
      fieldName: field.name,
      productName: field.hybrid.name,
      bulkPrefix
    });

    return {
      name,
      activityType: activity.type,
      activityUuid: activity.uuid,
      attributes: { crop: 'corn' },
      bases: [{ name: 'auto' }],
      tag: 'auto',
      primary: false,
      fieldId: field.legacyId,
      fieldUuid: field.id,
      updateTime: formatDateToISO8601(new Date()),
      fmzId: crypto.randomUUID(),
      fmzName: 'generated-fmz',
      fmzVersion: 0,
      id: prescriptionId,
      prescriptionId,
      summary: { zones: zonesSummaries },
      geometry: JSON.stringify({
        crs: { type: 'name', properties: { name: 'EPSG:4326' } },
        type: 'FeatureCollection',
        features
      })
    };
  });

  return prescriptionsToSave;
};

/**
 * Converts a Date object to an ISO 8601 date string (YYYY-MM-DD).
 * */
export const getDateString = (date: Date) => {
  return date.toISOString().split('T')[0];
};
